import { venueShortName } from "./venue-names.js";
const LONDON = "Europe/London";
/** All venue codes we know how to expand, longest-first so "UEL-SPORT-DOCK"
 *  matches before any shorter prefix. */
const VENUE_CODES = ["UEL-SPORT-DOCK", "JOSC", "MEPLS", "PBLC", "WSC"];
/** Matches an ISO-8601 instant with or without milliseconds / trailing Z. */
const ISO_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?Z?/g;
/** "Sun 5 Jul, 10:00" in London local time. Returns the raw input unchanged
 *  if it isn't a parseable instant. */
export function formatWhen(iso) {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms))
        return iso;
    const d = new Date(ms);
    const date = d.toLocaleDateString("en-GB", {
        timeZone: LONDON,
        weekday: "short",
        day: "numeric",
        month: "short",
    });
    const time = d.toLocaleTimeString("en-GB", {
        timeZone: LONDON,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    return `${date}, ${time}`;
}
function asString(v) {
    return typeof v === "string" && v.length > 0 ? v : undefined;
}
function asNumber(v) {
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
/** Replace every embedded ISO instant and venue code in a free-text message. */
export function prettifyMessage(message) {
    let out = message.replace(ISO_RE, (m) => formatWhen(m));
    for (const code of VENUE_CODES) {
        // Word-boundary replace so we never rewrite a code embedded in an id.
        const re = new RegExp(`\\b${code}\\b`, "g");
        out = out.replace(re, venueShortName(code));
    }
    // Trim a trailing space left before a full stop after substitutions.
    return out.replace(/\s+\./g, ".").trim();
}
/** Structured templates for the noisy midnight-booker court events. Returns
 *  undefined when the entry has no dedicated template (→ generic prettifier). */
function templated(entry) {
    const m = entry.metadata ?? {};
    const venue = asString(m.venueId);
    const venueName = venue ? venueShortName(venue) : undefined;
    const start = asString(m.startTime);
    const when = start ? formatWhen(start) : undefined;
    const court = asNumber(m.courtNumber);
    switch (entry.type) {
        case "session.booking.success":
        case "court.booked":
            if (court !== undefined && venueName && when)
                return `Booked court ${court} at ${venueName} · ${when}.`;
            return undefined;
        case "court.lease_failed":
        case "court.confirm_failed": {
            const err = asString(m.error);
            if (venueName && when)
                return `Couldn't get a court at ${venueName} · ${when}${err ? ` — ${err}` : ""}.`;
            return undefined;
        }
        case "court.excluded": {
            const wanted = asNumber(m.wanted);
            const available = asNumber(m.available);
            if (venueName && when && wanted !== undefined && available !== undefined)
                return `Only ${available} of ${wanted} courts free at ${venueName} · ${when}.`;
            return undefined;
        }
        case "cap.suppressed": {
            const by = asNumber(m.by);
            if (venueName && when && by !== undefined)
                return `Held back ${by} court${by === 1 ? "" : "s"} at ${venueName} · ${when} (over your limit).`;
            return undefined;
        }
        case "venue.dropped": {
            const reason = asString(m.reason);
            if (venueName)
                return `Skipped ${venueName}${reason ? ` — ${reason}` : ""}.`;
            return undefined;
        }
        default:
            return undefined;
    }
}
/** Human-readable message for a log entry, used by every log surface. */
export function humanizeAuditEntry(entry) {
    return templated(entry) ?? prettifyMessage(entry.message);
}
